import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  App as AntApp,
  Form,
  Input,
  Button,
  Typography,
  Upload,
  Image,
  Card,
  Flex,
  Divider,
} from "antd";
import { InboxOutlined, SaveOutlined } from "@ant-design/icons";

import { TiptapEditor } from "../../../TiptapEditor/TiptapEditor";
import { AssistanceApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { NEW_ASISTENTES } from "../../../../ui/featureFlags";
import { initialValues, validationSchema } from "./AssistanceForm.form";
import { useTranslation } from "react-i18next";

import styles from "./AssistanceForm.module.scss";

const { Title, Text } = Typography;
const { Dragger } = Upload;

function AssistanceFormInner(props) {
  const { onClose, onReload, assistance } = props;
  const { accessToken, user } = useAuth();
  const { message } = AntApp.useApp();
  const { t } = useTranslation("assistants");

  const assistanceController = useMemo(() => new AssistanceApi(), []);
  const [form] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);

  const previewUrlRef = useRef(null);

  useEffect(() => {
    const iv = initialValues(assistance);
    form.setFieldsValue(iv);

    // si vienes con miniature existente, no seteamos fileList
    setFileList([]);
  }, [assistance, form]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  const handleUploadChange = (info) => {
    const nextList = (info.fileList || []).slice(-1);
    setFileList(nextList);

    const fileObj = nextList[0]?.originFileObj;
    if (!fileObj) return;

    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = URL.createObjectURL(fileObj);

    form.setFieldValue("file", fileObj);
    form.setFieldValue("miniature", previewUrlRef.current);
  };

  const handleFinish = async () => {
    try {
      setSubmitting(true);

      const values = form.getFieldsValue(true);

      // Validación Yup (mapeamos errores a AntD)
      await validationSchema().validate(values, { abortEarly: false });

      const payload = { ...values };

      // Si no hay file nuevo, eliminamos el campo para evitar ruido
      if (!payload.file) delete payload.file;

      if (assistance?._id) {
        await assistanceController.updateAssistance(
          accessToken,
          assistance._id,
          payload,
        );
        message.success(t("form.successUpdate"));
      } else {
        await assistanceController.createAssistance(
          accessToken,
          payload,
          user.user._id,
        );
        message.success(t("form.successCreate"));
      }

      if (typeof onReload === "function") onReload();
      if (typeof onClose === "function") onClose();
    } catch (err) {
      if (err && err.inner && Array.isArray(err.inner) && err.inner.length) {
        const fields = err.inner.map((e) => ({
          name: e.path,
          errors: [e.message],
        }));
        form.setFields(fields);
        message.error(t("form.errorFields"));
      } else {
        // eslint-disable-next-line no-console
        console.error(err);
        message.error(t("form.errorSave"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const miniature = Form.useWatch("miniature", form);

  return (
    <Card
      className={styles.card}
      variant="outlined"
      styles={{ body: { padding: 16 } }}
    >
      <Title level={4} className={styles.title}>
        {assistance ? t("form.titleEdit") : t("form.title")}
      </Title>
      <Text type="secondary">{t("form.subtitle")}</Text>

      <Divider style={{ margin: "12px 0" }} />

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={handleFinish}
      >
        <div className={styles.grid2}>
          <Form.Item
            label={t("form.nameLabel")}
            name="title"
            rules={[{ required: true, message: t("form.nameRequired") }]}
          >
            <Input size="large" placeholder={t("form.namePlaceholder")} />
          </Form.Item>

          <Form.Item
            label={t("form.centerLabel")}
            name="path"
            rules={[{ required: true, message: t("form.centerRequired") }]}
          >
            <Input size="large" placeholder={t("form.centerPlaceholder")} />
          </Form.Item>
        </div>

        <Form.Item
          label={t("form.roleLabel")}
          name="role"
          rules={[{ required: true, message: t("form.roleRequired") }]}
        >
          <div className={styles.editor}>
            <TiptapEditor
              content={form.getFieldValue("role")}
              onChange={(val) => form.setFieldValue("role", val)}
            />
          </div>
        </Form.Item>

        <Form.Item
          label={t("form.exercisesLabel")}
          name="ejercicios"
          rules={[{ required: true, message: t("form.exercisesRequired") }]}
        >
          <div className={styles.editor}>
            <TiptapEditor
              content={form.getFieldValue("ejercicios")}
              onChange={(val) => form.setFieldValue("ejercicios", val)}
            />
          </div>
        </Form.Item>

        <Form.Item
          label={t("form.avatarLabel")}
          name="miniature"
          rules={[{ required: true, message: t("form.avatarRequired") }]}
        >
          <div className={styles.uploadWrap}>
            <Dragger
              multiple={false}
              maxCount={1}
              accept=".jpg,.jpeg,.png"
              beforeUpload={() => false}
              fileList={fileList}
              onChange={handleUploadChange}
              className={styles.dragger}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">{t("form.uploadText")}</p>
              <p className="ant-upload-hint">{t("form.uploadHint")}</p>
            </Dragger>

            {miniature ? (
              <div className={styles.preview}>
                <Image
                  src={miniature}
                  alt="Avatar"
                  width={140}
                  height={140}
                  style={{ objectFit: "cover" }}
                />
              </div>
            ) : null}
          </div>
        </Form.Item>

        <Flex justify="end" gap={10} wrap className={styles.footer}>
          <Button onClick={onClose} disabled={submitting}>
            {t("form.cancelButton")}
          </Button>
          <Button
            htmlType="submit"
            type={NEW_ASISTENTES ? "primary" : undefined}
            loading={submitting}
            icon={<SaveOutlined />}
            className={NEW_ASISTENTES ? undefined : styles.primaryBtn}
          >
            {assistance ? t("form.submitUpdate") : t("form.submitCreate")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
}

export function AssistanceForm(props) {
  return (
    <AntApp>
      <AssistanceFormInner {...props} />
    </AntApp>
  );
}
